'use client';

import { ReactNode, useCallback, useEffect, useState } from 'react';

import { cn } from 'cn';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'use-intl';

import { FEATURE_FLAGS, usePosthogFeatureFlag } from '@/shared-measurement';
import { getExternalEmbedSandbox } from '@/shared-utilities';

import { browserSessionAtom } from './project-usability-browser-session.atoms';
import { useHyperbeamResize } from './project-usability-hyperbeam-resize.hooks';
import {
  useBrowserSessionCleanup,
  useHyperbeamBackButtonTrap,
  useHyperbeamInstance,
  useIframeEmbeddabilityCheck,
} from './project-usability-hyperbeam.hooks';
import ProjectUsabilityResponseIframePlaceholder from './project-usability-response-iframe-placeholder';
import {
  calculateRenderingState,
  teardownHyperbeamOnConversationOver,
} from './project-usability-response-iframe.helpers';
import { usePageOrigin } from './project-usability-response-iframe.hooks';

interface Props {
  contentUrl: string | null;
  containerClassName?: string;
  isConversationOver?: boolean;
  /** True when mounted inside the manager's preview dialog; disables the Back-button trap whose `pushState` sentinel would close the dialog via TanStack Router's location subscribers. */
  isPreviewMode?: boolean;
  /** Rendered inside the iframe's container (on top of the iframe/hyperbeam). Use for question overlays like Image Choice. */
  children?: ReactNode;
}

const ProjectUsabilityResponseIframe = ({
  contentUrl,
  containerClassName,
  isConversationOver = false,
  isPreviewMode = false,
  children,
}: Props) => {
  const t = useTranslations('Usability');
  const { flag: flagHyperbeam } = usePosthogFeatureFlag(
    FEATURE_FLAGS.HYPERBEAM_FEATURE
  );
  const isHyperbeamEnabled = flagHyperbeam(true, false) ?? false;

  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [iframeLoadedFor, setIframeLoadedFor] = useState<string | null>(null);

  const embedderOrigin = usePageOrigin();

  const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node);
  }, []);

  const cleanup = useBrowserSessionCleanup();

  // Check if we already have an active browser session
  const browserSession = useAtomValue(browserSessionAtom);
  const hasActiveSession = !!browserSession.instance;

  // Only check embeddability if Hyperbeam is enabled and no active session exists
  // If Hyperbeam is disabled, skip check and always use iframe
  const { canEmbed, isChecking } = useIframeEmbeddabilityCheck(
    isHyperbeamEnabled && !hasActiveSession ? contentUrl : null
  );

  const {
    hyperbeamInstance,
    isLoading: isHyperbeamLoading,
    error,
  } = useHyperbeamInstance({
    contentUrl,
    containerElement,
  });

  useHyperbeamResize({
    hyperbeamInstance,
    containerElement,
  });

  useHyperbeamBackButtonTrap(
    isConversationOver || isPreviewMode ? null : hyperbeamInstance
  );

  const { showIframe, showHyperbeam, shouldFallbackToIframe } =
    calculateRenderingState({
      isHyperbeamEnabled,
      hasActiveSession,
      isChecking,
      isHyperbeamLoading,
      canEmbed,
      error,
      contentUrl,
      isConversationOver,
    });

  const isIframeLoaded = iframeLoadedFor === contentUrl;
  const isIframeContentReady = showIframe && !!contentUrl && isIframeLoaded;
  const isHyperbeamContentReady = showHyperbeam && !isHyperbeamLoading;
  const showErrorState = !!error && !shouldFallbackToIframe;

  const showPlaceholder =
    !isConversationOver &&
    !showErrorState &&
    !isIframeContentReady &&
    !isHyperbeamContentReady;

  // Cleanup on conversation over
  useEffect(
    () =>
      teardownHyperbeamOnConversationOver({
        isConversationOver,
        hasActiveSession,
        cleanup,
      }),
    [isConversationOver, hasActiveSession, cleanup]
  );

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (hasActiveSession) {
        void cleanup();
      }
    };
  }, [hasActiveSession, cleanup]);

  // Cleanup on tab/window close
  useEffect(() => {
    if (!hasActiveSession) {
      return;
    }

    const handlePageHide = () => {
      // Cleanup function clears atom first, then destroys instance
      // API cleanup may not complete before page unloads, but that's acceptable
      cleanup().catch(() => {
        // Ignore errors - page is unloading
      });
    };

    // pagehide is more reliable than unload, especially on mobile
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [hasActiveSession, cleanup]);

  return (
    <div
      data-test='usability-response-iframe-container'
      className={cn(
        'relative flex w-full flex-col gap-4 overflow-hidden rounded-4xl bg-slate-100',
        containerClassName
      )}
    >
      {/* Regular iframe (only if no session exists and URL is embeddable) */}
      {showIframe && contentUrl && (
        <iframe
          key={embedderOrigin ?? 'ssr'}
          title={t('iframeTitle')}
          src={contentUrl}
          sandbox={getExternalEmbedSandbox(contentUrl, {
            allowPopupsToEscapeSandbox: true,
            embedderOrigin,
          })}
          referrerPolicy='no-referrer'
          className='size-full rounded-4xl'
          data-test='usability-iframe'
          onLoad={() => setIframeLoadedFor(contentUrl)}
        />
      )}

      {showPlaceholder && (
        <div
          className='absolute inset-0 z-10'
          data-test='usability-iframe-placeholder-wrapper'
        >
          <ProjectUsabilityResponseIframePlaceholder />
        </div>
      )}

      {/* Hyperbeam (if session exists or URL is not embeddable) */}
      {showHyperbeam && (
        <div
          ref={containerCallbackRef}
          className='size-full'
          data-test='hyperbeam-container'
        />
      )}

      {/* Error State - only show if not falling back to iframe */}
      {error && !shouldFallbackToIframe && (
        <div
          className='absolute inset-0 z-10 flex items-center justify-center bg-slate-100'
          data-test='error-state'
        >
          <div className='text-red-500'>{error}</div>
        </div>
      )}

      {children}
    </div>
  );
};

export default ProjectUsabilityResponseIframe;
